import { Helmet } from "react-helmet-async"

interface SEOHeadProps {
  title: string
  description: string
  ogImage?: string
  ogImageAlt?: string
  ogUrl?: string
  themeColor?: string
  /** Canonical URL; also used as og:url when ogUrl is not given. */
  canonical?: string
  /** Value for <html lang>. */
  htmlLang?: string
  /** og:locale, e.g. "en_US". */
  ogLocale?: string
  siteName?: string
  /** Language alternates (hreflang), including an "x-default" entry if wanted. */
  alternates?: { hrefLang: string; href: string }[]
  /** Schema.org structured data, rendered as application/ld+json. */
  jsonLd?: object | object[]
}

export function SEOHead({
  title,
  description,
  ogImage,
  ogImageAlt,
  ogUrl,
  themeColor,
  canonical,
  htmlLang,
  ogLocale,
  siteName,
  alternates,
  jsonLd,
}: SEOHeadProps) {
  const url = ogUrl || canonical || (typeof window !== "undefined" ? window.location.href : "")

  return (
    <Helmet htmlAttributes={htmlLang ? { lang: htmlLang } : undefined}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {themeColor && <meta name="theme-color" content={themeColor} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {alternates?.map(alt => (
        <link key={alt.hrefLang} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
      ))}

      <meta property="og:type" content="website" />
      {siteName && <meta property="og:site_name" content={siteName} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {url && <meta property="og:url" content={url} />}
      {ogLocale && <meta property="og:locale" content={ogLocale} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}

      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  )
}
